"use client";
import { useState, useEffect, useRef } from "react";
import { X, Clock, Gift, ArrowRight } from "lucide-react";
import { Button } from "@ui/button";
import { usePathname } from "next/navigation";
import { APP_SIGNUP_URL } from "@/client/components/shared";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const LandingPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const pathname = usePathname();
  const landingPath = "/landing/natural-medicine-pathway";
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true);
      requestAnimationFrame(() => setIsVisible(true));
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // When the modal opens, move focus to it so keyboard/SR users can act on it. [SC 2.4.3]
  useEffect(() => {
    if (isOpen) closeBtnRef.current?.focus();
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => setIsOpen(false), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      handleClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusable =
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleApply = () => {
    window.open(APP_SIGNUP_URL, "_blank", "noopener,noreferrer");
    handleClose();
  };

  if (!isOpen || pathname !== landingPath) return null;

  return (
    // Backdrop closes on click as a pointer convenience; keyboard users have
    // Escape and the visible Close button.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? "bg-black/60 backdrop-blur-sm" : "bg-transparent"}`}
      onClick={handleClose}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="landing-popup-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        className={`relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl transition-all duration-500 ${isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-90 translate-y-8"}`}
      >
        {/* Top decorative band */}
        <div className="h-2 bg-gradient-to-r from-healing-500 via-nature-500 to-healing-600" />

        {/* Close button */}
        <button
          ref={closeBtnRef}
          type="button"
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 rounded-full p-1.5 bg-white/80 hover:bg-white text-healing-700 transition-colors shadow-sm"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* Content */}
        <div className="bg-white p-8 text-center">
          {/* Celebration icon */}
          <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-gradient-to-br from-healing-100 to-nature-100 flex items-center justify-center shadow-inner">
            <Gift className="h-8 w-8 text-healing-600 animate-[fadeUp_0.6s_ease-out_0.3s_both]" aria-hidden="true" />
          </div>

          <h3 id="landing-popup-title" className="font-serif-display text-2xl font-bold text-healing-900 mb-3 animate-[fadeUp_0.5s_ease-out_0.4s_both]">
            Congrats on taking the first step!
          </h3>

          <p className="text-healing-800/80 leading-relaxed mb-6 animate-[fadeUp_0.5s_ease-out_0.5s_both]">
            Complete your application within{" "}
            <span className="font-bold text-healing-700">48 hours</span> to lock
            in your{" "}
            <span className="font-semibold text-nature-700">
              discounted application fee
            </span>{" "}
            and a{" "}
            <span className="font-semibold text-nature-700">
              special tuition
            </span>
            .
          </p>

          {/* Urgency indicator */}
          <div className="flex items-center justify-center gap-2 mb-6 text-sm text-healing-600 bg-healing-50 rounded-full py-2 px-4 mx-auto w-fit animate-[fadeUp_0.5s_ease-out_0.6s_both]">
            <Clock className="h-4 w-4 animate-pulse" aria-hidden="true" />
            <span className="font-medium">Limited-time offer</span>
          </div>

          {/* CTA */}
          <Button
            onClick={handleApply}
            className="w-full bg-healing-600 hover:bg-healing-700 text-white font-semibold py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group animate-[fadeUp_0.5s_ease-out_0.7s_both]"
          >
            Apply Now
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Button>

          <p className="text-xs text-muted-foreground mt-4 animate-[fadeUp_0.5s_ease-out_0.8s_both]">
            No commitment required — start your application today.
          </p>
        </div>
      </div>
    </div>
  );
};
